import * as THREE from 'three/webgpu';
import { SceneAuxiliaryObjectRegistry } from '../../scene-object';
import { Subject, type Observable } from 'rxjs';
import {
    type PatternDirectionArrowAxisDragEvent,
    type PatternDirectionArrowAxisDragOptions,
} from './PatternDirectionArrowAxisDragTypes';

/**
 * 方向箭头视觉更新参数。
 */
export interface PatternDirectionArrowVisualInput {
    /** 锚点世界坐标（选中对象的中心点）。 */
    anchorWorld: THREE.Vector3;
    /** 方向向量（世界坐标，无需预先归一化）。 */
    directionWorld: THREE.Vector3;
    /** 杆身半径（世界尺寸）。 */
    shaftRadius: number;
    /** 杆身长度（世界尺寸）。 */
    shaftLength: number;
    /** 箭头锥头半径（世界尺寸）。 */
    headRadius: number;
    /** 箭头锥头长度（世界尺寸）。 */
    headLength: number;
    /**
     * 箭头底部长度（沿 -Z 方向，局部空间）。
     * 该值越大，箭头在锚点下方保留的“下面部分”越长。
     */
    bottomLength?: number;
    /**
     * 箭头顶部杆身长度（沿 +Z 方向，局部空间，不含锥头）。
     * 该值越大，箭头在锚点上方的“上面部分”越长。
     */
    topLength?: number;
    /**
     * 锚点沿方向的上浮偏移。
     * 用于将箭头整体抬离选中特征面/线，避免视觉穿插。
     */
    anchorLift?: number;
}


/**
 * 方向箭头轴向拖拽运行时状态。
 */
export interface PatternDirectionArrowAxisDragState {
    /** 是否启用轴向拖拽。 */
    enabled: boolean;
    /** 拖拽上下文标识。 */
    contextKey: string;
    /** 起始业务标量值。 */
    scalarValue: number;
    /** 场景单位到业务标量单位的换算比例。 */
    sceneUnitToScalarScale: number;
}


/**
 * 线性零部件阵列方向箭头对象。
 *
 * 设计说明：
 * 1. 继承 THREE.Group，由杆身（CylinderGeometry）和锥头（ConeGeometry）组成；
 * 2. 组内默认轴向为 +Z，通过四元数旋转对齐至目标方向；
 * 3. `skipSelection = true` 确保该对象不会被纳入常规选择射线检测链；
 * 4. `depthTest: false` 使箭头始终渲染在其他对象之上，便于方向指示识别；
 * 5. 资源释放通过 `dispose()` 统一回收 geometry 和 material。
 */
export class PatternDirectionArrowObject extends THREE.Group {
    /** 运行时类型标记，供外层过滤用。 */
    public readonly isPatternDirectionArrowObject = true;

    /**
     * 跳过常规选择链，确保鼠标拾取时不会意外选中箭头本身。
     * 与 ExtrudeHandlePartGroup 保持同一约定。
     */
    public readonly skipSelection = true;

    /** 杆身网格。 */
    private readonly shaftMesh: THREE.Mesh;

    /** 锥头网格。 */
    private readonly headMesh: THREE.Mesh;

    /**
     * 组内默认方向轴（+Z）。
     * 与 extrude-handle-gizmo 保持一致，通过四元数 setFromUnitVectors 对齐目标方向。
     */
    private readonly defaultAxis = new THREE.Vector3(0, 0, 1);

    /** 位置复用对象。 */
    private readonly tempPosition = new THREE.Vector3();

    /** 当前箭头使用的颜色，供管理器判断是否需要重建。 */
    public readonly color: number;

    
    /** 轴向拖拽事件流。 */
    private readonly axisDragSubject = new Subject<PatternDirectionArrowAxisDragEvent>();

    /** 当前轴向拖拽状态。 */
    private axisDragState: PatternDirectionArrowAxisDragState = {
        enabled: false,
        contextKey: '',
        scalarValue: 0,
        sceneUnitToScalarScale: 1,
    };

    /** 最近一次视觉更新的锚点世界坐标。 */
    private readonly axisDragAnchorWorld = new THREE.Vector3();

    /** 最近一次视觉更新的方向世界坐标。 */
    private readonly axisDragDirectionWorld = new THREE.Vector3(0, 0, 1);
    

    /**
     * 创建方向箭头对象。
     *
     * @param color 箭头颜色（十六进制）。
     */
    public constructor(color: number) {
        super();
        // 独立使用方向箭头时也保持辅助身份，不依赖管理器是否已经挂载。
        SceneAuxiliaryObjectRegistry.markRoot(this);
        this.color = color;
        this.shaftMesh = this.createShaft(color);
        this.headMesh = this.createHead(color);
        this.add(this.shaftMesh);
        this.add(this.headMesh);
    }

    /**
     * 更新箭头的位置、朝向和尺寸。
     *
     * 坐标约定（组局部空间，+Z 为默认轴向）：
    * - 杆身不再对称分布，尾部只保留较短一段；
    * - 箭头整体沿方向向前上浮，确保底端位于选中特征面/线之上；
    * - 锥头底面与杆身前端对齐，锥头尖端朝 +Z。
     *
     * @param input 更新参数。
     */
    public updateVisual(input: PatternDirectionArrowVisualInput): void {
        const dir = input.directionWorld.clone().normalize();
        // 方向长度为零时跳过，避免四元数求解异常。
        if (dir.lengthSq() <= 1e-12) {
            return;
        }

        
        // 记录拖拽轴的最新世界空间定义，让交互控制器无需反查几何局部状态。
        this.axisDragAnchorWorld.copy(input.anchorWorld);
        this.axisDragDirectionWorld.copy(dir);
        

        // 将整个组旋转到目标方向。
        this.quaternion.setFromUnitVectors(this.defaultAxis, dir);

        const bottomLength = Math.max(0, input.bottomLength ?? (input.shaftLength * 0.12));
        const topLength = Math.max(0, input.topLength ?? (input.shaftLength - bottomLength));
        // 固定世界尺寸下限会在高倍相机 zoom 时把屏幕像素手柄重新放大，仅防止长度精确退化为零。
        const shaftLength = Math.max(Number.EPSILON, bottomLength + topLength);
        const anchorLift = Math.max(0, input.anchorLift ?? 0);

        // 将组沿方向前置，使箭头底端位于锚点“上方”而非穿过选中特征。
        this.tempPosition.copy(input.anchorWorld).addScaledVector(dir, anchorLift);
        this.position.copy(this.tempPosition);

        // ---- 杆身：CylinderGeometry 默认沿 Y，通过 rotation.x = PI/2 使其沿 Z ----
        // scale.set(r, length, r) 先缩放，rotation 再旋转，最终杆身沿 Z 延伸 shaftLength。
        this.shaftMesh.scale.set(input.shaftRadius, shaftLength, input.shaftRadius);
        // 杆身范围为 [-bottomLength, topLength]。
        this.shaftMesh.position.set(0, 0, (topLength - bottomLength) * 0.5);

        // ---- 锥头：ConeGeometry 默认沿 Y，通过 rotation.x = PI/2 使其沿 +Z 指向 ----
        this.headMesh.scale.set(input.headRadius, input.headLength, input.headRadius);
        // 锥头底面对齐杆身前端（topLength）。
        this.headMesh.position.set(
            0,
            0,
            topLength + input.headLength * 0.5,
        );
    }

    /**
     * 释放箭头所有 GPU 资源。
     * 调用后对象不可再使用，需由管理器负责从场景中移除。
     */
    public dispose(): void {
        this.disposeMesh(this.shaftMesh);
        this.disposeMesh(this.headMesh);
        
        // 对象销毁时完成事件流，避免外部订阅在预览对象反复重建时泄漏。
        this.axisDragSubject.complete();
        
    }

    
    /**
     * 配置箭头轴向拖拽能力。
     *
     * @param options 拖拽配置。
     */
    public setAxisDragOptions(options: PatternDirectionArrowAxisDragOptions): void {
        this.axisDragState = {
            enabled: options.enabled,
            contextKey: options.contextKey ?? '',
            scalarValue: Number.isFinite(options.scalarValue) ? options.scalarValue ?? 0 : 0,
            sceneUnitToScalarScale: Number.isFinite(options.sceneUnitToScalarScale)
                ? options.sceneUnitToScalarScale ?? 1
                : 1,
        };
    }

    /**
     * 读取当前轴向拖拽状态。
     *
     * @returns 拖拽状态快照。
     */
    public getAxisDragState(): PatternDirectionArrowAxisDragState {
        return { ...this.axisDragState };
    }

    /**
     * 判断当前箭头是否允许轴向拖拽。
     *
     * @returns 启用时返回 `true`。
     */
    public isAxisDragEnabled(): boolean {
        return this.axisDragState.enabled;
    }

    /**
     * 读取当前拖拽轴定义。
     *
     * @returns 轴原点与方向的克隆值。
     */
    public getAxisDragFrame(): { anchorWorld: THREE.Vector3; directionWorld: THREE.Vector3 } {
        return {
            anchorWorld: this.axisDragAnchorWorld.clone(),
            directionWorld: this.axisDragDirectionWorld.clone(),
        };
    }

    /**
     * 观察当前箭头的轴向拖拽事件。
     *
     * @returns 轴向拖拽事件流。
     */
    public observeAxisDrag(): Observable<PatternDirectionArrowAxisDragEvent> {
        return this.axisDragSubject.asObservable();
    }

    /**
     * 派发轴向拖拽事件。
     *
     * @param event 事件载荷。
     */
    public emitAxisDrag(event: PatternDirectionArrowAxisDragEvent): void {
        this.axisDragSubject.next(event);
    }

    

    /**
     * 创建杆身网格。
     *
     * 使用 CylinderGeometry (r=1, height=1, Y轴)，
     * 配合 rotation.x = PI/2 使其运行于 Z 轴。
     *
     * @param color 杆身颜色。
     * @returns 杆身网格对象。
     */
    private createShaft(color: number): THREE.Mesh {
        const geometry = new THREE.CylinderGeometry(1, 1, 1, 12);
        const material = new THREE.MeshStandardMaterial({
            color,
            metalness: 0.30,
            roughness: 0.62,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.85,
        });
        const mesh = new THREE.Mesh(geometry, material);
        // CylinderGeometry 默认沿 Y，旋转 PI/2 使其沿 +Z。
        mesh.rotation.x = Math.PI * 0.5;
        mesh.renderOrder = 82;
        return mesh;
    }

    /**
     * 创建锥头网格。
     *
     * 使用 ConeGeometry (r=1, height=1, Y轴)，
     * 配合 rotation.x = PI/2 使尖端朝向 +Z。
     *
     * @param color 锥头颜色。
     * @returns 锥头网格对象。
     */
    private createHead(color: number): THREE.Mesh {
        const geometry = new THREE.ConeGeometry(1, 1, 16);
        const material = new THREE.MeshStandardMaterial({
            color,
            metalness: 0.42,
            roughness: 0.58,
            emissive: 0x20242a,
            emissiveIntensity: 0.02,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.92,
        });
        const mesh = new THREE.Mesh(geometry, material);
        // ConeGeometry 默认尖端朝 +Y，旋转 PI/2 使尖端朝 +Z。
        mesh.rotation.x = Math.PI * 0.5;
        mesh.renderOrder = 83;
        return mesh;
    }

    /**
     * 释放单个网格的 geometry 和 material 资源。
     *
     * @param mesh 目标网格。
     */
    private disposeMesh(mesh: THREE.Mesh): void {
        mesh.geometry.dispose();
        const material = mesh.material;
        if (Array.isArray(material)) {
            material.forEach((item) => item.dispose());
        } else {
            material.dispose();
        }
    }
}
